import { Box, Button, Stack, Typography } from '@mui/material';

import type { IntakePracticeVerification } from '../../types';
import { IntakePill } from '../shell/IntakePill';
import { cardSx, type PillTone, wf } from '../shell/tokens';

const verifyCardSx = { ...cardSx, padding: { xs: '26px 20px', md: '34px 30px 32px' } } as const;

const primaryButtonSx = {
  bgcolor: wf.blue,
  color: wf.surface,
  fontSize: 14,
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '11px 22px',
  boxShadow: 'none',
  '&:hover': { bgcolor: wf.blueHover, boxShadow: 'none' },
} as const;

/** The design's submitted stamp, from real data only. */
function submittedLabel(submittedAt?: string | null): string | null {
  if (!submittedAt) return null;
  const parsed = new Date(submittedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return `Submitted ${parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function VerificationCard({
  tone,
  pill,
  title,
  children,
}: {
  tone: PillTone;
  pill: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={verifyCardSx}>
      <Box sx={{ maxWidth: 640 }}>
        <IntakePill tone={tone} uppercase label={pill} />
        <Typography
          component="h2"
          sx={{
            mt: '14px',
            fontSize: { xs: 19, md: 22 },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: wf.ink,
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function VerificationBody({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ mt: '8px', maxWidth: 640, fontSize: 14, color: '#52526a', lineHeight: 1.65 }}
    >
      {children}
    </Typography>
  );
}

/** EHR migration does not wait on verification, so we offer it while the review runs. */
function WhileYouWaitBanner({ onOpenEhr }: { onOpenEhr?: () => void }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{
        alignItems: { sm: 'center' },
        bgcolor: wf.blueBanner,
        border: `1px solid ${wf.blueLine}`,
        borderRadius: '8px',
        padding: '14px 18px',
        mt: '18px',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: wf.ink }}>
          While you wait: bring your patient records
        </Typography>
        <Typography sx={{ mt: '3px', fontSize: 13, color: '#4a4a5c', lineHeight: 1.55 }}>
          EHR migration doesn’t depend on verification. Upload your export now and it’s done by the
          time your payers are.
        </Typography>
      </Box>
      {onOpenEhr && (
        <Button
          onClick={onOpenEhr}
          variant="contained"
          disableElevation
          sx={{ ...primaryButtonSx, fontSize: 13, padding: '10px 16px', whiteSpace: 'nowrap' }}
        >
          Upload EHR export
        </Button>
      )}
    </Stack>
  );
}

/** Completion is supplied only from the server journey verification. */
export function IntakeVerificationStatus({
  verified = false,
  practiceVerification,
  onOpenStatus,
  onOpenEhr,
}: {
  verified?: boolean;
  practiceVerification?: IntakePracticeVerification | null;
  /** Navigate to Intake Status once verification is done. Omit to hide the button. */
  onOpenStatus?: () => void;
  /** Navigate to the EHR migration track. Omit to hide the banner's button. */
  onOpenEhr?: () => void;
}) {
  if (practiceVerification) {
    const counts = practiceVerification.provider_counts;
    const complete = practiceVerification.state === 'verified';
    const awaiting = practiceVerification.state === 'awaiting';
    const submitted = submittedLabel(practiceVerification.submitted_at);
    return (
      <VerificationCard
        tone={complete ? 'green' : awaiting ? 'indigo' : 'amber'}
        pill={complete ? 'Verified' : awaiting ? 'In review' : 'Incomplete'}
        title={
          complete
            ? 'Your practice’s intake is verified'
            : awaiting
              ? 'JotPsych is verifying your practice’s intake'
              : 'Your practice’s intake is incomplete'
        }
      >
        <VerificationBody>
          {complete
            ? 'You can now view your practice’s payer connections.'
            : counts.incomplete > 0
              ? 'Complete intake for the remaining providers. Submitted providers can be reviewed while the others finish.'
              : 'Our team is reviewing the submitted intake. This status will update after review.'}
        </VerificationBody>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 2 }}
          sx={{
            alignItems: { sm: 'center' },
            borderTop: `1px solid ${wf.lineSoft}`,
            mt: '24px',
            pt: '20px',
          }}
        >
          <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, color: wf.muted, lineHeight: 1.6 }}>
              {counts.verified} of {counts.total} providers verified
            </Typography>
            {!complete && (
              <Typography sx={{ fontSize: 13, color: wf.muted, lineHeight: 1.6 }}>
                {counts.awaiting} awaiting review · {counts.incomplete} intake incomplete
              </Typography>
            )}
          </Box>
          {submitted && (
            <Typography sx={{ fontSize: 13, color: wf.meta, whiteSpace: 'nowrap' }}>
              {submitted}
            </Typography>
          )}
        </Stack>
        {complete
          ? onOpenStatus && (
              <Button
                onClick={onOpenStatus}
                variant="contained"
                disableElevation
                sx={{ ...primaryButtonSx, mt: '20px' }}
              >
                Go to intake status →
              </Button>
            )
          : <WhileYouWaitBanner onOpenEhr={onOpenEhr} />}
      </VerificationCard>
    );
  }
  if (verified) {
    return (
      <VerificationCard tone="green" pill="Verified" title="Your intake checked out">
        <VerificationBody>
          Our team verified your intake against every payer you picked. Intake Status is open — from
          here, each payer moves at its own pace and we file everything by hand.
        </VerificationBody>
        {onOpenStatus && (
          <Button
            onClick={onOpenStatus}
            variant="contained"
            disableElevation
            sx={{ ...primaryButtonSx, mt: '20px' }}
          >
            Go to intake status →
          </Button>
        )}
      </VerificationCard>
    );
  }
  return (
    <VerificationCard tone="indigo" pill="In review" title="JotPsych is verifying your intake">
      <VerificationBody>
        A person on our enrollment team is reading your intake against what each payer requires.
        We’ll let you know the moment we’re done — after that, the waiting is on your payers, not on
        us.
      </VerificationBody>
      <Box sx={{ borderTop: `1px solid ${wf.lineSoft}`, mt: '24px', pt: '20px' }}>
        <Typography sx={{ fontSize: 13, color: wf.muted, lineHeight: 1.6 }}>
          Nothing is needed from you right now. If a check turns up something only you can answer,
          we’ll reach out with that one question instead of sending you back through the form.
        </Typography>
      </Box>
      <WhileYouWaitBanner onOpenEhr={onOpenEhr} />
    </VerificationCard>
  );
}
